import AbstractSession from "../../shared/session/AbstractSession";
import AdminContext from "../AdminContext";
import Administration from "./Administration";
import Exception from "../../shared/exception/Exception";
import ErrorCode from "../../shared/exception/ErrorCode";
import Message from "../../shared/log/Message";
import {MessageType} from "../../shared/log/MessageInterface";
import TokenHandler from "../../shared/session/token/TokenHandler";
import AdminTokenHandler from "./AdminTokenHandler";
import Translate from "../../shared/translation/Translate";
import StorageItem from "../../shared/StorageItem";
import OAuthError from "../../shared/exception/OAuthError";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import {LoginOptions, UserCredentials} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";

export default class AdminSession extends AbstractSession<AdminContext, AdminSession> {
	public async initialize(): Promise<void> {
		await this.info.fetch();

		let tokenHandler: TokenHandler = new AdminTokenHandler(this.context);

		try {
			await tokenHandler.initialize();
			let administration: Administration = this.getAdministration();
			administration.context = this.context;
			await administration.initialize();
		} catch (exception: any) {
			let portalException: Exception = Exception.parseException(exception);
			sessionStorage.setItem(StorageItem.OAUTH_ERROR, JSON.stringify({
				name: portalException.name,
				code: portalException.code,
				description: portalException.message,
				raw: portalException.stack
			} as OAuthError));

			this.clearSessionToken();
			this.context.getPage().setState({
				showAdminLoginDialog: true,
				adminLoginParameter: undefined
			});
		}
	}

	/**
	 * Login into the server with user and password
	 */
	async login(options?: LoginOptions, username?: string, password?: string): Promise<void> {
		await super.login(options, username, password);

		if (!FunctionHelper.applyValue(this.user?.isAdmin, UserCredentials.getIsAdminDefault())) {
			throw new Exception(Translate.value("errors.admin.user", {ns: "message"}));
		}

		let administration: Administration = this.getAdministration();
		await administration.initialize();

		this.context.getPage().setState({
			context: this.context
		});
	}

	public async handleException(exception: Error, showMessage?: boolean): Promise<Exception> {
		let portalException: Exception = Exception.parseException(exception);

		if (portalException.name === "ERR_NETWORK") {
			await this.reconnectServer();
			return portalException;
		}

		switch (portalException.code) {
			case ErrorCode.WEBSERVICE_SESSION_TOKEN_NOT_FOUND:
			case ErrorCode.WEBSERVICE_SESSION_EXPIRED:
			case ErrorCode.WEBSERVICE_SESSION_UNABLE_TO_CREATE:
			case ErrorCode.WEBSERVICE_SESSION_TOKEN_NOT_ALLOWED:
			case ErrorCode.WEBSERVICE_SESSION_UNKNOWN_ERROR:
			case ErrorCode.WEBSERVICE_SESSION_TOKEN_NOT_PROVIDED:
				this.clearSessionToken();
				this.context.getPage().setState({
					showAdminLoginDialog: true,
					adminLoginParameter: undefined
				});
				break;
			case ErrorCode.WEBSERVICE_SESSION_TIMEOUT:
				try {
					await this.refresh();

					// no need to show timeout messages
					return portalException;
				} catch (exception: any) {
					return await this.handleException(exception, showMessage);
				}
		}

		let message: Message = new Message(portalException.message, MessageType.DANGER);
		message.code = portalException.code;
		message.visible = FunctionHelper.applyValue(showMessage, true);
		this.context.getPage().addMessage(message);

		return portalException;
	}

	public getAdministration(): Administration {
		return super.getAdministration() as Administration;
	}

	protected createAdministration(): Administration {
		return new Administration(this);
	}
}